<template>
  <div
    ref="mobileSelectRef"
    class="mobile-select"
    :class="{
      'mobile-select--open': isOpen,
      'mobile-select--dropdown-top': showDropdownAtTop
    }"
  >
    <div
      ref="headerRef"
      class="mobile-select__header"
      @click="toggleDropdown"
    >
      <div class="mobile-select__selected">
        <img
          v-if="selectedCategory?.attributes.icon?.data?.attributes.url"
          :src="getUploadUrl(selectedCategory?.attributes.icon?.data?.attributes.url)"
          alt="selectedCategory.attributes.name"
        />
        {{ selectedCategory?.attributes.name }}
      </div>
      <div class="mobile-select__arrow"></div>
    </div>

    <transition :name="'dropdown'">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="mobile-select__dropdown"
        :style="{ top: `calc(100% + 5px + ${dropdownTop}px)` }"
      >
        <div
          ref="scrollContainerRef"
          class="mobile-select__scroll-container"
          :style="{ maxHeight: `${dropdownHeight}px` }"
        >
          <div
            v-for="category in categories"
            :key="category.id"
            class="mobile-select__item"
            :class="{ 'mobile-select__item--active': selectedCategory?.id === category.id }"
            @click="selectItem(category.id)"
          >
            {{ category.attributes.name }}
          </div>

          <div
            class="mobile-select__item mobile-select__item--clear"
            @click="clearSelection"
          >
            Очистить выбор
          </div>
        </div>
      </div>
    </transition>

    <div
      v-if="isOpen"
      class="mobile-select__backdrop"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, onUnmounted } from 'vue';
  import { useBodyScroll } from '@/composables/useBodyScroll';
  import { getUploadUrl } from '@/helpers/urls';
  import { Category } from '@/services/category/api';

  const props = defineProps<{
    categories: Category[] | null;
    selectedCategoryId: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'select', categoryId: number): void;
    (e: 'clear'): void;
  }>();

  const { lockScroll, unlockScroll } = useBodyScroll();

  // Рефы для DOM элементов
  const mobileSelectRef = ref<HTMLElement | null>(null);
  const headerRef = ref<HTMLElement | null>(null);
  const dropdownRef = ref<HTMLElement | null>(null);
  const scrollContainerRef = ref<HTMLElement | null>(null);

  // Реактивные переменные состояния
  const isOpen = ref(false);
  const showDropdownAtTop = ref(false); // Флаг для отображения дропдауна вверху

  const dropdownTop = ref(0);
  const dropdownHeight = ref(0);
  const selectedCategory = computed(() =>
    props.categories?.find(category => category.id === props.selectedCategoryId)
  );

  function calculateDropdownHeightNew(scrollDifference: number) {
    if (!mobileSelectRef.value) {
      return;
    }

    // получаем текущую позицию элемента
    const elementPosition = mobileSelectRef.value.getBoundingClientRect().top + window.scrollY;

    const newElementPosition = elementPosition - (window.scrollY - scrollDifference);

    const elementHeight = 54;

    const dropdownAbsoluteTopPosition = newElementPosition + elementHeight;

    const newHeight = window.innerHeight - dropdownAbsoluteTopPosition - 16;

    const MIN_HEIGHT = 400;

    if (newHeight < MIN_HEIGHT) {
      dropdownHeight.value = MIN_HEIGHT;
      dropdownTop.value = newHeight - MIN_HEIGHT;
    } else {
      dropdownHeight.value = newHeight;
      dropdownTop.value = 0;
    }
  }

  const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
  };

  const closeDropdown = () => {
    isOpen.value = false;
  };

  const selectItem = (categoryId: number) => {
    emit('select', categoryId);
    closeDropdown();
  };

  const clearSelection = () => {
    emit('clear');
    closeDropdown();
  };

  // Обработчик изменения размера окна
  const handleResize = () => {
    if (isOpen.value) {
      calculateDropdownHeightNew(0);
    }
  };

  // // После обновления DOM, если dropdown открыт и рефы обновились, пересчитываем высоту
  // onUpdated(() => {
  //   if (isOpen.value && scrollContainerRef.value) {
  //     calculateDropdownHeightNew(0);
  //   }
  // });

  watch(isOpen, newVal => {
    if (newVal && mobileSelectRef.value) {
      const headerOffset = 120;
      const elementPosition = mobileSelectRef.value.getBoundingClientRect().top + window.scrollY;

      const offsetPosition = elementPosition - headerOffset;

      const scrollDifference = window.scrollY - offsetPosition;

      const spaceLeft = window.document.body.scrollHeight - (window.scrollY + window.innerHeight);

      if (scrollDifference < 0) {
        if (spaceLeft < scrollDifference * -1) {
          calculateDropdownHeightNew(-spaceLeft);
        } else {
          calculateDropdownHeightNew(scrollDifference);
        }
      } else {
        calculateDropdownHeightNew(scrollDifference);
      }

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Добавляем слушатель изменения размера окна при открытии списка
      window.addEventListener('resize', handleResize);
    } else {
      // Удаляем слушатель, когда список закрыт
      window.removeEventListener('resize', handleResize);
    }

    if (newVal) {
      lockScroll();
    } else {
      unlockScroll();
    }
  });

  // Удаляем слушатель при размонтировании компонента
  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    unlockScroll();
  });
</script>

<style scoped>
  .mobile-select {
    position: relative;
    width: 100%;
    margin-bottom: 20px;
    /* z-index: 3; */

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px;
      background: #fff;
      border: 2px solid var(--g-color-gray);
      border-radius: 15px;
      cursor: pointer;
      user-select: none;
      transition: all 0.3s ease;
    }

    &--open {
      .mobile-select__header {
        border-color: var(--g-color-orange);
        box-shadow: 0 0 10px rgba(var(--g-color-orange-rgb), 0.2);
      }

      .mobile-select__arrow {
        transform: rotate(225deg) translateY(-5px);
      }
    }

    &__selected {
      font-weight: 500;
      font-size: 16px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &__arrow {
      width: 12px;
      height: 12px;
      border-right: 2px solid var(--g-color-gray);
      border-bottom: 2px solid var(--g-color-gray);
      transform: rotate(45deg);
      transition: transform 0.3s ease;
      margin-top: -6px;
    }

    &__dropdown {
      position: absolute;
      top: calc(100% + 5px);
      left: 0;
      width: 100%;
      background: #fff;
      border-radius: 15px;
      box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
      z-index: 11;
      overflow: hidden; /* Скрываем все, что выходит за пределы закругленных краев */
    }

    /* Стили для дропдауна, отображаемого сверху */
    &--dropdown-top {
      .mobile-select__dropdown {
        top: auto;
        bottom: calc(100% + 5px);
      }
    }

    &__scroll-container {
      width: 100%;
      max-height: 100%;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      /* Добавляем небольшой отступ справа чтобы скроллбар не прилипал вплотную к краю */
      padding-right: 2px;

      /* Стили для скроллбара */
      scrollbar-width: thin;
      scrollbar-color: var(--g-color-orange) #f1f1f1;

      /* Стили для скроллбара в WebKit браузерах */
      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
        margin: 8px 0; /* Небольшой отступ сверху и снизу */
      }

      &::-webkit-scrollbar-thumb {
        background: var(--g-color-orange);
        border-radius: 10px;
      }
    }

    &__item {
      padding: 15px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-bottom: 1px solid rgba(var(--g-color-gray-rgb), 0.2);
      flex-shrink: 0;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: rgba(var(--g-color-orange-rgb), 0.1);
      }

      &--active {
        background-color: rgba(var(--g-color-orange-rgb), 0.2);
        font-weight: 500;
      }

      &--clear {
        text-align: center;
        color: var(--g-color-orange);
        font-weight: 500;
        margin-top: 5px;
        border-top: 1px solid rgba(var(--g-color-gray-rgb), 0.2);
      }
    }

    &__backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 10;
    }
  }

  /* Анимации для дропдауна снизу */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.3s ease;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  /* Анимации для дропдауна сверху */
  .dropdown-top-enter-active,
  .dropdown-top-leave-active {
    transition: all 0.3s ease;
  }

  .dropdown-top-enter-from,
  .dropdown-top-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }
</style>
